import { useEffect, useRef } from 'react'
import * as THREE from 'three'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import {
  defaultExtents,
  getModelsBbox,
  localFaceNormal,
  localToWorldDir,
  localToWorldPoint,
  type FaceKey,
} from './sectionMath'
import { IDENTITY_ORIENTATION } from '@viewer/store/sectionSlice'
import { constanteDe } from './planosDeCorte'

// Pushes 6 inward-facing clipping planes derived from the OBB
// (orientation + local extents) into renderer.clippingPlanes whenever
// the section box is enabled. The wireframe and arrows live in the
// SVG/HTML overlay.
//
// Os modelos do Fragments lêem este mesmo array (ver
// `ligarCortesAoModelo` no viewerApi): é assim que o raio da
// pré-selecção e do clique ignora a parte cortada.
export function useSectionBox() {
  const enabled = useAppStore((s) => s.sectionEnabled)
  // Os planos soltos entram no mesmo array do renderizador. Quem
  // escreve o `clippingPlanes` tem de ser um só: se a caixa e os planos
  // escrevessem cada um por si, o último a correr apagava o outro.
  const soltos = useAppStore((s) => s.planosDeCorte)
  const orientation = useAppStore((s) => s.sectionOrientation)
  const extents = useAppStore((s) => s.sectionExtents)
  const setOrientation = useAppStore((s) => s.setSectionOrientation)
  const setExtents = useAppStore((s) => s.setSectionExtents)
  const setRange = useAppStore((s) => s.setSectionExtentRange)
  const planesRef = useRef<THREE.Plane[]>(
    Array.from({ length: 6 }, () => new THREE.Plane()),
  )

  // Initialise from active model bbox the first time the user enables
  // the section box. Defaults to identity orientation (axis-aligned).
  useEffect(() => {
    if (!enabled) return
    if (orientation && extents) return
    const box = getModelsBbox()
    if (!box) return
    const ext = defaultExtents(box)
    setOrientation(IDENTITY_ORIENTATION)
    setExtents(ext)
    setRange(ext)
  }, [enabled, orientation, extents, setOrientation, setExtents, setRange])

  // Derive the 6 clipping planes from the current OBB, and append the
  // free ones.
  useEffect(() => {
    const world = viewerApi.getWorld()
    if (!world) return
    const renderer = world.renderer!.three
    const livres = soltos
      .filter((p) => p.activo)
      .map((p) =>
        new THREE.Plane(
          new THREE.Vector3(p.normal[0], p.normal[1], p.normal[2]),
          constanteDe(p),
        ),
      )
    if (!enabled || !orientation || !extents) {
      renderer.clippingPlanes = livres
      viewerApi._invalidate()
      // O raio do Fragments corre sobre a última vista que o worker
      // recebeu: sem pedir uma vista nova, os planos novos só contavam
      // para a pré-selecção depois de a câmara se mexer.
      viewerApi.actualizarModelos()
      return
    }
    const planes = planesRef.current
    const faces: FaceKey[] = [
      'minX',
      'maxX',
      'minY',
      'maxY',
      'minZ',
      'maxZ',
    ]
    faces.forEach((face, i) => {
      // Inward-facing normal in world coords. Outward normal of -X
      // face in local frame is (-1,0,0) → inward is (+1,0,0).
      const localOutward = localFaceNormal(face)
      const localInward: [number, number, number] = [
        -localOutward[0],
        -localOutward[1],
        -localOutward[2],
      ]
      const worldInward = localToWorldDir(orientation, localInward).normalize()
      // Point on the plane: face centre transformed to world.
      const cx = (extents.minX + extents.maxX) / 2
      const cy = (extents.minY + extents.maxY) / 2
      const cz = (extents.minZ + extents.maxZ) / 2
      const localPoint: [number, number, number] = (() => {
        switch (face) {
          case 'minX':
            return [extents.minX, cy, cz]
          case 'maxX':
            return [extents.maxX, cy, cz]
          case 'minY':
            return [cx, extents.minY, cz]
          case 'maxY':
            return [cx, extents.maxY, cz]
          case 'minZ':
            return [cx, cy, extents.minZ]
          case 'maxZ':
            return [cx, cy, extents.maxZ]
        }
      })()
      const worldPoint = localToWorldPoint(orientation, localPoint)
      planes[i].setFromNormalAndCoplanarPoint(worldInward, worldPoint)
    })
    renderer.clippingPlanes = [...planes, ...livres]
    viewerApi._invalidate()
    viewerApi.actualizarModelos()
  }, [enabled, orientation, extents, soltos])

  // Tear down on unmount.
  useEffect(() => {
    return () => {
      const world = viewerApi.getWorld()
      if (!world) return
      world.renderer!.three.clippingPlanes = []
      viewerApi.actualizarModelos()
    }
  }, [])
}
